import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { useDocuments, useSubscription, useUploadDocument } from '@/hooks/useApi';
import toast from 'react-hot-toast';
import { AxiosError } from 'axios';
import PageSkeleton from '@/components/PageSkeleton';
import Pagination from '@/components/Pagination';
import api from '@/lib/api';

const DEFAULT_MAX_UPLOAD_BYTES = 10 * 1024 * 1024; // safe fallback if /subscription not yet loaded

export default function DocumentsPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Number(searchParams.get('page')) || 1;
  const debouncedSearch = searchParams.get('search') ?? '';
  const [search, setSearch] = useState(debouncedSearch);
  const mimeType = searchParams.get('mimeType') ?? '';
  const documentType = searchParams.get('documentType') ?? '';
  const sort = searchParams.get('sort') ?? 'createdAt';
  const direction = (searchParams.get('direction') ?? 'desc') as 'asc' | 'desc';
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const { data: documents, isLoading, refetch } = useDocuments({
    page,
    search: debouncedSearch || undefined,
    mimeType: mimeType || undefined,
    documentType: documentType || undefined,
    sort,
    direction,
  });
  const { data: subscription } = useSubscription();
  const uploadDoc = useUploadDocument();
  const fileRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  const maxUploadBytes = subscription?.maxDocumentUploadBytes
    ?? documents?.meta?.maxUploadBytes
    ?? DEFAULT_MAX_UPLOAD_BYTES;
  const maxUploadLabel = formatSize(maxUploadBytes);
  const usedBytes = documents?.meta?.usedBytes ?? 0;

  function setPage(p: number) {
    const next = new URLSearchParams(searchParams);
    if (p <= 1) next.delete('page');
    else next.set('page', String(p));
    setSearchParams(next);
  }

  function setParam(key: string, value: string) {
    const next = new URLSearchParams(searchParams);
    if (value) next.set(key, value);
    else next.delete(key);
    // Filters reset paging.
    next.delete('page');
    setSearchParams(next);
  }

  const handleSearchChange = (value: string) => {
    setSearch(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setParam('search', value);
    }, 300);
  };

  function toggleSort(field: string) {
    const next = new URLSearchParams(searchParams);
    if (sort === field) {
      next.set('direction', direction === 'asc' ? 'desc' : 'asc');
    } else {
      next.set('sort', field);
      next.set('direction', 'asc');
    }
    next.delete('page');
    setSearchParams(next);
  }

  function sortIndicator(field: string) {
    if (sort !== field) return '';
    return direction === 'asc' ? ' ▲' : ' ▼';
  }

  function handleUpload(files: FileList | null) {
    if (!files || files.length === 0) return;

    Array.from(files).forEach((file) => {
      // Pre-flight validation: refuse empty or oversized files before upload.
      if (file.size === 0) {
        toast.error(t('documents.uploadEmpty', { name: file.name }));
        return;
      }
      if (file.size > maxUploadBytes) {
        toast.error(
          t('documents.uploadTooLarge', {
            name: file.name,
            size: formatSize(file.size),
            max: maxUploadLabel,
          }),
          { duration: 6000 },
        );
        return;
      }

      const formData = new FormData();
      formData.append('file', file);

      const toastId = toast.loading(
        t('documents.uploadStarting', { name: file.name }),
      );
      const startedAt = Date.now();
      let lastUpdate = 0;

      uploadDoc.mutate(
        {
          formData,
          onUploadProgress: ({ loaded, total }) => {
            // Throttle UI updates to ~5/s to avoid spamming react-hot-toast.
            const now = Date.now();
            if (now - lastUpdate < 200 && loaded !== total) return;
            lastUpdate = now;

            const elapsed = (now - startedAt) / 1000; // seconds
            const percent = total ? Math.round((loaded / total) * 100) : 0;
            const speed = elapsed > 0 ? loaded / elapsed : 0; // bytes/s
            const remaining =
              total && speed > 0 ? Math.max(0, (total - loaded) / speed) : 0;
            const eta = remaining > 0 ? `${Math.ceil(remaining)}s` : '…';

            toast.loading(
              total
                ? t('documents.uploadProgress', {
                    name: file.name,
                    percent,
                    eta,
                  })
                : t('documents.uploadStarting', { name: file.name }),
              { id: toastId },
            );
          },
        },
        {
          onSuccess: () => {
            const elapsed = ((Date.now() - startedAt) / 1000).toFixed(1);
            toast.success(
              t('documents.uploadDone', { name: file.name, elapsed }),
              { id: toastId },
            );
            refetch();
          },
          onError: (err) => {
            // Server-side rejection (413) — surface the plan/server limit.
            const axiosErr = err as AxiosError<{ error?: string; maxLabel?: string }>;
            if (axiosErr?.response?.status === 413) {
              const serverMax = axiosErr.response.data?.maxLabel ?? maxUploadLabel;
              toast.error(
                t('documents.uploadTooLarge', {
                  name: file.name,
                  size: formatSize(file.size),
                  max: serverMax,
                }),
                { id: toastId, duration: 6000 },
              );
              return;
            }
            toast.error(t('documents.uploadFailed', { name: file.name }), {
              id: toastId,
            });
          },
        },
      );
    });
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault();
    setDragging(false);
    handleUpload(e.dataTransfer.files);
  }

  async function handleDownload(docId: string, fileName: string) {
    try {
      const res = await api.get(`/documents/${docId}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error('Download failed');
    }
  }

  async function handleDelete(docId: string) {
    if (!confirm(t('common.confirmDelete'))) return;
    try {
      await api.delete(`/documents/${docId}`);
      toast.success(t('common.deleted'));
      refetch();
    } catch {
      toast.error('Delete failed');
    }
  }

  function fileIcon(mimeType: string) {
    if (mimeType?.startsWith('image/')) return '🖼';
    if (mimeType?.includes('pdf')) return '📄';
    if (mimeType?.includes('spreadsheet') || mimeType?.includes('excel')) return '📊';
    return '📎';
  }

  function formatSize(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1048576).toFixed(1)} MB`;
  }

  if (isLoading) return <PageSkeleton />;

  return (
    <div>
      <div className="page-header">
        <h1 className="text-2xl font-bold">{t('documents.title')}</h1>
        <button className="btn btn-primary" onClick={() => fileRef.current?.click()}>
          + {t('documents.upload')}
        </button>
        <input ref={fileRef} type="file" className="hidden" multiple onChange={(e) => handleUpload(e.target.files)} />
      </div>

      {/* Drop zone */}
      <div
        className={`card card-body mb-6 border-2 border-dashed text-center py-8 transition ${dragging ? 'border-primary-400 bg-primary-50' : 'border-gray-200'}`}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
      >
        <p className="text-gray-500">{t('documents.dropHere')}</p>
        <p className="text-xs text-gray-400 mt-1">{t('documents.uploadHint', { max: maxUploadLabel })}</p>
      </div>

      {/* Storage usage + filters */}
      <div className="card card-body mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div className="text-sm text-gray-600">
          <span className="font-medium text-gray-900">{formatSize(usedBytes)}</span>{' '}
          {t('documents.usedTotal')}{' '}
          <span className="text-gray-400">·</span>{' '}
          {t('documents.perFileLimit', { max: maxUploadLabel })}
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <input
            type="search"
            value={search}
            onChange={(e) => handleSearchChange(e.target.value)}
            placeholder={t('documents.searchPlaceholder')}
            className="rounded-lg border border-gray-200 px-3 py-1.5 text-sm w-48"
          />
          <select
            value={mimeType}
            onChange={(e) => setParam('mimeType', e.target.value)}
            className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          >
            <option value="">{t('documents.allKinds')}</option>
            <option value="application/pdf">PDF</option>
            <option value="image/">{t('documents.kindImage')}</option>
            <option value="application/vnd.openxmlformats-officedocument.spreadsheetml">Excel</option>
            <option value="application/vnd.ms-excel">XLS</option>
            <option value="text/csv">CSV</option>
            <option value="application/zip">ZIP</option>
          </select>
          <select
            value={documentType}
            onChange={(e) => setParam('documentType', e.target.value)}
            className="rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
          >
            <option value="">{t('documents.allTypes')}</option>
            <option value="receipt">{t('documents.typeReceipt')}</option>
            <option value="invoice">{t('documents.typeInvoice')}</option>
            <option value="contract">{t('documents.typeContract')}</option>
            <option value="other">{t('documents.typeOther')}</option>
          </select>
          {(search || mimeType || documentType
            || sort !== 'createdAt' || direction !== 'desc') && (
            <button
              type="button"
              onClick={() => { setSearch(''); setSearchParams(new URLSearchParams()); }}
              className="text-xs text-gray-500 hover:text-gray-900 underline"
            >
              {t('common.reset')}
            </button>
          )}
        </div>
      </div>

      {/* Documents list */}
      <div className="card overflow-x-auto">
        <table className="table">
          <thead>
            <tr>
              <th>
                <button type="button" onClick={() => toggleSort('originalName')} className="font-semibold hover:text-gray-900">
                  {t('documents.name')}{sortIndicator('originalName')}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => toggleSort('mimeType')} className="font-semibold hover:text-gray-900">
                  {t('documents.type')}{sortIndicator('mimeType')}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => toggleSort('fileSize')} className="font-semibold hover:text-gray-900">
                  {t('documents.size')}{sortIndicator('fileSize')}
                </button>
              </th>
              <th>
                <button type="button" onClick={() => toggleSort('createdAt')} className="font-semibold hover:text-gray-900">
                  {t('documents.uploaded')}{sortIndicator('createdAt')}
                </button>
              </th>
              <th className="w-20" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading && Array.from({ length: 3 }).map((_, i) => (
              <tr key={i}>{Array.from({ length: 5 }).map((_, j) => <td key={j}><div className="skeleton h-4 w-20" /></td>)}</tr>
            ))}
            {documents?.data?.map((doc) => {
              const mime = doc.mimeType || '';
              const rowColor = mime.startsWith('image/')
                ? 'row-violet'
                : mime === 'application/pdf'
                ? 'row-red'
                : mime.includes('spreadsheet') || mime.includes('excel') || mime.includes('csv')
                ? 'row-emerald'
                : mime.includes('word') || mime.startsWith('text/')
                ? 'row-blue'
                : 'row-gray';
              return (
              <tr key={doc.id} className={rowColor}>
                <td className="font-medium text-gray-900">
                  <span className="mr-2">{fileIcon(doc.mimeType)}</span>
                  {doc.originalName}
                </td>
                <td className="text-gray-500 text-sm">{doc.mimeType}</td>
                <td className="text-gray-500 text-sm">{formatSize(doc.fileSize)}</td>
                <td className="text-gray-500 text-sm">{new Date(doc.createdAt).toLocaleDateString()}</td>
                <td>
                  <div className="flex gap-1">
                    <button className="btn btn-secondary btn-sm" onClick={() => handleDownload(doc.id, doc.originalName)}>↓</button>
                    <button className="btn btn-secondary btn-sm text-red-500" onClick={() => handleDelete(doc.id)}>✕</button>
                  </div>
                </td>
              </tr>
              );
            })}
            {!isLoading && (!documents?.data || documents.data.length === 0) && (
              <tr><td colSpan={5} className="text-center text-gray-400 py-12">{t('common.noResults')}</td></tr>
            )}
          </tbody>
        </table>
        <Pagination meta={documents?.meta} onPageChange={setPage} />
      </div>
    </div>
  );
}
